import { find, map } from 'lodash';
import { mergeObjects } from '../object';

/**
 * @param array
 * @param element
 * @param identity
 * @returns new array which consists of the merged value between the given
 * element and the array element by it's identity property
 *
 * For example:
 *
 * [array]: [{id: '1', value: '1'}, {id: '2', value: '2'}]
 * [element]: {id: '2', value: '3'}
 *
 * mergeById(array, element): [{id: '1', value: '1'}, {id: '2', value: '3'}]
 */
export const mergeById = (array, element, identity = 'id') =>
    map(array, (iteratedElement) =>
        iteratedElement[identity] === element[identity] ? mergeObjects(iteratedElement, element) : iteratedElement
    );

/**
 * @param array
 * @param elements
 * @param identity
 * @returns new array which consists of the merged value between the given
 * element and the array element by it's identity property
 *
 * For example:
 *
 * [array]: [{id: '1', value: '1'}, {id: '2', value: '2'}]
 * [elements]: [{id: '2', value: '3'}, {id: '4', value: '4'}]
 *
 * unionById(array, element): [{id: '1', value: '1'}, {id: '2', value: '3'}, {id: '4', value: '4'}]
 */
export const unionById = (array, elements, identity = 'id') =>
    map(elements, (mergedElement) => {
        const originalElement = findById(array, mergedElement[identity]);

        return {
            ...originalElement,
            ...mergedElement,
        };
    });

/**
 * @param array
 * @param id
 * @param identity
 * @returns element in the array which corresponds to the given id
 *
 * For example:
 *
 * [array]: [{id: '1', value: '1'}, {id: '2', value: '2'}]
 * [id]: '2'
 *
 * findById(array, id): {id: '2', value: '2'}
 */
export const findById = (array, id, identity = 'id') =>
    find(array, (iteratedElement) => iteratedElement[identity] === id);

/**
 * Removes an element from the array which corresponds to the given id.
 *
 * @param array
 * @param id
 * @param identity
 */
export const removeById = (array = [], id, identity = 'id') => array.filter((element) => element[identity] !== id);

/**
 * Returns the last element id in the array
 *
 * @param array
 * @param identity
 */
export const getLastId = (array = [], identity = 'id') => array[array.length - 1][identity];
